import { useCallback } from "react";
import {
  useAudioNotificationStore,
  NotificationSoundType,
} from "../stores/audioNotificationStore";

// Re-export for backwards compatibility
export type { NotificationSoundType } from "../stores/audioNotificationStore";

/**
 * Generates a notification tone using Web Audio API
 * Different patterns for each notification type
 */
function playGeneratedTone(
  audioContext: AudioContext,
  soundType: NotificationSoundType,
  volume: number
): void {
  const masterGain = audioContext.createGain();
  masterGain.connect(audioContext.destination);
  masterGain.gain.value = volume * 0.3; // Scale down to prevent harsh sounds

  const now = audioContext.currentTime;

  switch (soundType) {
    case "newOrder": {
      // Pleasant ascending two-tone chime (like a cash register)
      const frequencies = [523.25, 659.25]; // C5, E5
      frequencies.forEach((freq, i) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.connect(gain);
        gain.connect(masterGain);
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + i * 0.15);
        gain.gain.linearRampToValueAtTime(1, now + i * 0.15 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.15 + 0.3);
        osc.start(now + i * 0.15);
        osc.stop(now + i * 0.15 + 0.35);
      });
      break;
    }
    case "newRequest": {
      // Single alert tone with slight vibrato
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(masterGain);
      osc.type = "sine";
      osc.frequency.value = 880; // A5
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(1, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
      osc.start(now);
      osc.stop(now + 0.45);
      break;
    }
    case "newChat": {
      // Soft double-beep (like message received)
      [0, 0.12].forEach((delay) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.connect(gain);
        gain.connect(masterGain);
        osc.type = "sine";
        osc.frequency.value = 1046.5; // C6
        gain.gain.setValueAtTime(0, now + delay);
        gain.gain.linearRampToValueAtTime(0.8, now + delay + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.01, now + delay + 0.1);
        osc.start(now + delay);
        osc.stop(now + delay + 0.12);
      });
      break;
    }
    case "kdsNewOrder": {
      // Loud double-beep for noisy kitchen (800Hz + 1000Hz square wave)
      const kdsFreqs = [800, 1000];
      kdsFreqs.forEach((freq, i) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        osc.connect(gain);
        gain.connect(masterGain);
        osc.type = "square";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, now + i * 0.2);
        gain.gain.linearRampToValueAtTime(1.5, now + i * 0.2 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.2 + 0.25);
        osc.start(now + i * 0.2);
        osc.stop(now + i * 0.2 + 0.3);
      });
      break;
    }
  }
}

/**
 * Hook for managing audio notifications
 *
 * Uses Web Audio API to generate notification tones programmatically.
 * No external sound files required.
 *
 * State and AudioContext are shared across all components via Zustand store,
 * ensuring toggling audio in the Topbar affects all notification sources.
 *
 * IMPORTANT: The AudioContext must be created during user interaction (e.g., clicking
 * the audio toggle button). Once created, it can be used by SignalR event handlers.
 *
 * Provides:
 * - settings: Current audio notification settings
 * - setSettings: Update settings
 * - toggle: Toggle enabled state
 * - play: Play a notification sound
 * - playTestSound: Play a short test beep
 */
export function useAudioNotifications() {
  const { settings, setSettings, toggle } = useAudioNotificationStore();

  const play = useCallback((soundType: NotificationSoundType) => {
    // Read current state directly from store to ensure we have latest enabled status
    const store = useAudioNotificationStore.getState();
    const currentSettings = store.settings;

    if (!currentSettings.enabled) {
      return;
    }
    if (!currentSettings.sounds[soundType]) {
      return;
    }

    // Use shared AudioContext from store
    const ctx = store.getOrCreateAudioContext();
    if (!ctx) {
      return;
    }

    // If context is suspended, try to resume (though this may fail without user gesture)
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {
        // Ignore resume failures - browser may require user gesture
      });
    }

    try {
      playGeneratedTone(ctx, soundType, currentSettings.volume);
    } catch {
      // Ignore audio errors silently
    }
  }, []);

  /**
   * Play a short test sound to confirm audio is working
   * This should be called from a user interaction (button click) to initialize the AudioContext
   */
  const playTestSound = useCallback(() => {
    const store = useAudioNotificationStore.getState();
    // Creating/getting the AudioContext during user interaction ensures it's not suspended
    const ctx = store.getOrCreateAudioContext();
    if (!ctx) return;

    const currentSettings = store.settings;
    try {
      playGeneratedTone(ctx, "newOrder", currentSettings.volume);
    } catch {
      // Ignore errors
    }
  }, []);

  return {
    settings,
    setSettings,
    toggle,
    play,
    playTestSound,
  };
}

export default useAudioNotifications;
